/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { getAllPortals } from './constants';
import { buildScheduledAtPayloadField } from '@/shared/utils/portal-publish';
import type { PortalResult } from './publish-helpers';

/**
 * Identity translator used when only portal ids/backendIds are needed.
 * @param key - Portal label key or identifier string.
 * @returns The same key unchanged.
 */
function idKey(key: string): string {
  return key;
}

import { buildAdSpendConsent, type AdSpendConsent } from '@/shared/utils/ad-dialog-helpers';

export interface PublishPayloadParams {
  selected: string[];
  propertyId: string;
  hasImages: boolean;
  fbCreateLeadAd: boolean;
  fbLeadAdBudget: number;
  igCreateLeadAd?: boolean;
  igLeadAdBudget?: number;
  /** Days the Facebook / Instagram Lead Ad runs; sent explicitly so the user's consent covers the real total. */
  fbLeadAdDuration?: number;
  igLeadAdDuration?: number;
  scheduleEnabled: boolean;
  scheduledAt: string;
  /** The phone the OLX ad shows (digits), picked in the dialog. */
  olxPhone?: string | null;
  /** The main deal of the OLX ad (`sale` / `rental`) for a property offered for both; null when there was nothing to choose. */
  olxTransaction?: string | null;
}

/**
 * Builds the backend portals payload from selection.
 */
export function buildBackendPortals(selected: string[], hasImages: boolean): string[] {
  const catalog = getAllPortals(idKey);
  const mapped = selected
    .filter((id) => {
      if (id === 'instagram' && !hasImages) return false;
      const portal = catalog.find((p) => p.id === id);
      return portal && !portal.unavailable;
    })
    .map((id) => {
      const portal = catalog.find((p) => p.id === id);
      return portal?.backendId ?? id;
    });
  return Array.from(new Set(mapped));
}

/**
 * Attaches Facebook-specific options to the payload.
 */
function attachFacebookOptions(
  payload: Record<string, any>, selected: string[], fbCreateLeadAd: boolean, fbLeadAdBudget: number, fbLeadAdDuration: number,
) {
  if (!selected.includes('facebook_feed')) payload.fb_skip_feed = true;
  if (!selected.includes('facebook_marketplace')) payload.fb_skip_marketplace = true;
  if (fbCreateLeadAd) {
    payload.fb_create_lead_ad = true;
    payload.fb_lead_ad_budget = fbLeadAdBudget * 100;
    payload.fb_lead_ad_duration = fbLeadAdDuration;
  }
}

/**
 * Attaches Instagram-specific lead-ad options to the payload. Instagram ads
 * bill through the same Facebook ad account, so there's no per-portal
 * skip-feed/marketplace equivalent to attach here -- only the lead-ad opt-in.
 */
function attachInstagramOptions(
  payload: Record<string, any>, igCreateLeadAd: boolean, igLeadAdBudget: number, igLeadAdDuration: number,
) {
  if (igCreateLeadAd) {
    payload.ig_create_lead_ad = true;
    payload.ig_lead_ad_budget = igLeadAdBudget * 100;
    payload.ig_lead_ad_duration = igLeadAdDuration;
  }
}

/**
 * Builds the full publish request payload.
 */
export function buildPublishPayload(params: PublishPayloadParams): Record<string, any> | null {
  const {
    selected, propertyId, hasImages, fbCreateLeadAd, fbLeadAdBudget,
    igCreateLeadAd = false, igLeadAdBudget = 15, fbLeadAdDuration = 7, igLeadAdDuration = 7,
    scheduleEnabled, scheduledAt, olxPhone, olxTransaction,
  } = params;
  const backendPortals = buildBackendPortals(selected, hasImages);
  if (!backendPortals.length) return null;

  const payload: Record<string, any> = { property_id: propertyId, portals: backendPortals };
  if (backendPortals.includes('facebook')) attachFacebookOptions(payload, selected, fbCreateLeadAd, fbLeadAdBudget, fbLeadAdDuration);
  if (backendPortals.includes('instagram')) attachInstagramOptions(payload, igCreateLeadAd, igLeadAdBudget, igLeadAdDuration);
  if (backendPortals.includes('olx') && olxPhone) payload.olx_phone = olxPhone;
  if (backendPortals.includes('olx') && olxTransaction) payload.olx_transaction = olxTransaction;
  const scheduledIso = buildScheduledAtPayloadField(scheduleEnabled, scheduledAt);
  if (scheduledIso) payload.scheduled_at = scheduledIso;
  return payload;
}

/**
 * Adds the user's spend consent for every Lead Ad the payload requests. Call it
 * only after the user confirmed the spend dialog: the server refuses a Lead Ad
 * without a consent that matches its budget, duration and total, and records it.
 *
 * @param payload - The publish payload built by {@link buildPublishPayload}.
 * @returns The payload with `ad_spend_consent` (keyed by portal) when a Lead Ad is requested.
 */
export function withSpendConsent(payload: Record<string, any>): Record<string, any> {
  const consent: Record<string, AdSpendConsent> = {};
  if (payload.fb_create_lead_ad) {
    consent.facebook = buildAdSpendConsent(payload.fb_lead_ad_budget, payload.fb_lead_ad_duration);
  }
  if (payload.ig_create_lead_ad) {
    consent.instagram = buildAdSpendConsent(payload.ig_lead_ad_budget, payload.ig_lead_ad_duration);
  }
  return Object.keys(consent).length > 0 ? { ...payload, ad_spend_consent: consent } : payload;
}

const SUCCESS_RESULT_STATUSES = new Set(['published', 'queued', 'scheduled']);
const ERROR_RESULT_STATUSES = new Set(['error', 'failed']);

/**
 * Maps a publish result into a portal status.
 */
function resultToStatus(result: PortalResult): string | null {
  if (SUCCESS_RESULT_STATUSES.has(result.status)) return result.status;
  if (ERROR_RESULT_STATUSES.has(result.status)) return 'error';
  return null;
}

/**
 * Updates portal status from publish results.
 */
export function updateStatusFromResults(
  savedStatus: Record<string, string>,
  publishResults: Record<string, PortalResult>,
): Record<string, string> {
  const newStatus = { ...savedStatus };
  for (const [portal, result] of Object.entries(publishResults)) {
    const mapped = resultToStatus(result);
    if (mapped) newStatus[portal] = mapped;
  }
  return newStatus;
}
